/**
 * T6 — a VISÃO de um resultado DISC ou QA. O MESMO componente na tela final do link, no "Ver resultado" da ficha e
 * em "Meus testes" (pedido: "a visão Ver resultado usa o mesmo componente e a mesma ordem").
 *
 * DISC, na ordem aprovada pelo Matheus (o colaborador se reconhece ANTES de ver número):
 *   1. perfil principal + "como você é" · 2. qualidades e pontos de atenção (lado a lado; empilhados no celular) ·
 *   3. palavra de ordem em destaque · 4. segundo perfil, compacto · 5. as 4 barras a partir do zero + a leitura de
 *   cada uma · 6. "Conheça os quatro perfis", recolhível.
 * QA: barra por medida (0–25) com número e faixa, o total com faixa, a medida mais baixa e um parágrafo por medida.
 *
 * Textos: SÓ os do conteúdo aprovado (`TEXTOS`); os do resultado vêm do servidor, da versão gravada no resultado.
 */
import { Bar, BarChart, Cell, ReferenceLine, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { Card, LinhaRecolhivel } from '@/components/ui';
import { preencher, TEXTOS, textosDoResultado } from '@/lib/testes/textos';
import type { PerfilDisc, VisaoDisc, VisaoQa, VisaoResultado } from '@/lib/testes/tipos';

const LP = TEXTOS.link_publico;
/** As cores do gráfico vêm do tema (`tailwind.config.ts`): vermelho IGM e a tinta apagada. */
const COR_POSITIVA = '#B5261D';
const COR_NEGATIVA = '#8A8178';

const sinal = (n: number) => (n > 0 ? `+${n}` : String(n));

/** O texto literal com `{chave}` trocado pelo valor EM DESTAQUE (o resto do texto fica como está). */
function ComDestaque({ texto, chave, valor }: { texto: string; chave: string; valor: string }) {
  const [antes, depois = ''] = texto.split(`{${chave}}`);
  return (
    <>
      {antes}
      <span className="text-red">{valor}</span>
      {depois}
    </>
  );
}

function Titulo({ children }: { children: React.ReactNode }) {
  return <h3 className="font-display text-lg font-medium text-ink-1 dark:text-ink-dark-1">{children}</h3>;
}

type Textos = typeof LP;

function QualidadesEAtencao({ p, L, compacto = false }: { p: PerfilDisc; L: Textos; compacto?: boolean }) {
  const texto = compacto ? 'text-sm' : 'text-base';
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <Card variant="flat" className="p-4">
        <p className="text-xs font-semibold uppercase tracking-[0.08em] text-success">{L.titulo_qualidades}</p>
        <p className={`mt-1 leading-relaxed text-ink-1 dark:text-ink-dark-1 ${texto}`}>{p.pontosFortes}</p>
      </Card>
      <Card variant="flat" className="p-4">
        <p className="text-xs font-semibold uppercase tracking-[0.08em] text-warning">{L.titulo_atencao}</p>
        <p className={`mt-1 leading-relaxed text-ink-1 dark:text-ink-dark-1 ${texto}`}>{p.pontosDeAtencao}</p>
      </Card>
    </div>
  );
}

function ResultadoDisc({ v, L }: { v: VisaoDisc; L: Textos }) {
  return (
    <div className="space-y-6" data-testid="resultado-disc">
      {/* 1. Identificação */}
      <section>
        <h2 className="font-display text-2xl font-medium text-ink-1 dark:text-ink-dark-1 md:text-3xl" data-testid="perfil-principal">
          <ComDestaque texto={L.perfil_principal} chave="perfil" valor={v.principal.nome} />
        </h2>
        <p className="mt-2 text-base leading-relaxed text-ink-1 dark:text-ink-dark-1">{v.principal.comoVoceE}</p>
      </section>
      {/* 2. Qualidades e pontos de atenção */}
      <QualidadesEAtencao p={v.principal} L={L} />
      {/* 3. Palavra de ordem */}
      <Card variant="urgent" className="p-5">
        <p className="text-xs font-semibold uppercase tracking-[0.08em] text-ink-3">{L.titulo_palavra_ordem}</p>
        <p className="font-display text-4xl font-medium text-red" data-testid="palavra-de-ordem">{v.principal.palavraDeOrdem.palavra}</p>
        <p className="mt-1 text-base text-ink-1 dark:text-ink-dark-1">{v.principal.palavraDeOrdem.frase}</p>
      </Card>
      {/* 4. Segundo perfil, compacto */}
      <section className="space-y-2">
        <Titulo>{preencher(L.segundo_perfil, { perfil: v.segundo.nome })}</Titulo>
        <p className="text-sm leading-relaxed text-ink-2 dark:text-ink-dark-2">{v.segundo.comoVoceE}</p>
        <QualidadesEAtencao p={v.segundo} L={L} compacto />
      </section>
      {/* 5. As 4 barras a partir do zero + a leitura */}
      <section className="space-y-3">
        <div className="h-48 w-full" data-testid="grafico-disc">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={v.barras} layout="vertical" margin={{ top: 4, right: 32, bottom: 4, left: 8 }}>
              <XAxis type="number" domain={[-40, 40]} hide />
              <YAxis type="category" dataKey="nome" width={104} tickLine={false} axisLine={false} tick={{ fontSize: 13, fill: 'currentColor' }} />
              <ReferenceLine x={0} stroke={COR_NEGATIVA} />
              <Bar dataKey="pontos" isAnimationActive={false} label={{ position: 'right', fontSize: 13, fill: 'currentColor', formatter: (n: unknown) => sinal(Number(n)) }}>
                {v.barras.map((b) => (
                  <Cell key={b.letra} fill={b.pontos > 0 ? COR_POSITIVA : COR_NEGATIVA} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <Titulo>{L.titulo_leitura_barras}</Titulo>
        <ul className="space-y-2">
          {v.barras.map((b) => (
            <li key={b.letra} className="text-sm leading-relaxed text-ink-1 dark:text-ink-dark-1">
              <span className="font-semibold">{b.nome}</span> — {b.leitura}
            </li>
          ))}
        </ul>
      </section>
      {/* 6. Os quatro perfis */}
      <LinhaRecolhivel titulo={L.titulo_quatro_perfis} recolhe="sempre" data-testid="quatro-perfis">
        <div className="space-y-4">
          <p className="text-sm text-ink-2 dark:text-ink-dark-2">{L.intro_quatro_perfis}</p>
          {v.quatroPerfis.map((p) => (
            <div key={p.letra} className="space-y-1">
              <p className="font-semibold text-ink-1 dark:text-ink-dark-1">{p.nome}</p>
              <p className="text-sm text-ink-1 dark:text-ink-dark-1">{p.comoVoceE}</p>
              <p className="text-sm text-ink-2 dark:text-ink-dark-2">
                <span className="font-medium">{L.titulo_qualidades}:</span> {p.pontosFortes}
              </p>
              <p className="text-sm text-ink-2 dark:text-ink-dark-2">
                <span className="font-medium">{L.titulo_atencao}:</span> {p.pontosDeAtencao}
              </p>
            </div>
          ))}
        </div>
      </LinhaRecolhivel>
    </div>
  );
}

function ResultadoQa({ v, L }: { v: VisaoQa; L: Textos }) {
  return (
    <div className="space-y-6" data-testid="resultado-qa">
      <section className="space-y-3">
        {v.medidas.map((m) => (
          <div key={m.medida} data-testid={`qa-medida-${m.medida}`}>
            <div className="flex items-baseline justify-between gap-3">
              <span className="font-semibold text-ink-1 dark:text-ink-dark-1">{m.medida}</span>
              <span className="text-sm tabular-nums text-ink-2 dark:text-ink-dark-2">{preencher(L.qa_medida_linha, { pontos: m.pontos, faixa: m.faixaRotulo })}</span>
            </div>
            <div className="mt-1 h-2.5 w-full rounded-full bg-paper-warm dark:bg-paper-dark-warm" aria-hidden="true">
              <div className="h-2.5 rounded-full bg-red" style={{ width: `${(m.pontos / 25) * 100}%` }} />
            </div>
          </div>
        ))}
      </section>
      <Card variant="urgent" className="p-5">
        <p className="font-display text-2xl font-medium text-ink-1 dark:text-ink-dark-1" data-testid="qa-total">
          {preencher(L.qa_total, { pontos: v.total.pontos, faixa: v.total.faixaRotulo })}
        </p>
        <p className="mt-1 text-sm font-semibold text-red">{preencher(L.qa_medida_mais_baixa, { medida: v.maisBaixa })}</p>
        <p className="mt-2 text-base leading-relaxed text-ink-1 dark:text-ink-dark-1">{v.total.texto}</p>
      </Card>
      <section className="space-y-3">
        {v.medidas.map((m) => (
          <div key={m.medida}>
            <p className="font-semibold text-ink-1 dark:text-ink-dark-1">
              {m.medida} · {m.faixaRotulo}
            </p>
            <p className="text-sm leading-relaxed text-ink-1 dark:text-ink-dark-1">{m.texto}</p>
          </div>
        ))}
      </section>
    </div>
  );
}

/**
 * `paraGestor` (A1, 05/10): quem olha NÃO é o próprio funcionário → títulos em terceira pessoa ("Perfil principal",
 * "Qualidades"). Sem ele, o texto do funcionário ("Seu perfil principal"). A troca é `textosDoResultado`, uma regra só.
 */
export function ResultadoTeste({ visao, paraGestor = false }: { visao: VisaoResultado; paraGestor?: boolean }) {
  const L = textosDoResultado(paraGestor);
  return visao.tipo === 'disc' ? <ResultadoDisc v={visao} L={L} /> : <ResultadoQa v={visao} L={L} />;
}
